import React, { useState, useEffect, useContext, useRef } from 'react';
import Paper from '@mui/material/Paper';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import { APIContext } from '../Contexts/APIContext';
import { ClientInsightsViewModel } from './ViewModels/ClientInsightsViewModel';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, TableFooter } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
const styles = {
    root: {
        color: 'black',
    },
    paper: {
        backgroundColor: 'var(--colour-lilac)',
        padding: '8px',
    },
    table: {
        backgroundColor: '#fff',
    },
    bold: {
        fontWeight: 'bold',
    },
    error: {
        color: 'red',
    }
}

export default function ClientInsightsContainer() {
    const { get, post, isAdmin } = useContext(APIContext);

    const [insightListLoaded, setInsightListLoaded] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [insightList, setInsightList] = useState<ClientInsightsViewModel[]>([]);
    const originalInsightList = useRef<ClientInsightsViewModel[]>([]);
    const [newClient, setNewClient] = useState('');

    useEffect(() => {
        getClientInsights();
    }, []);

    useEffect(() => {
        const a = JSON.stringify(insightList);
        const b = JSON.stringify(originalInsightList.current);
        setUnsavedChanges(a !== b);
    }, [insightList]);

    const getClientInsights = () => {
        setInsightListLoaded(false);
        get('ClientInsights', `GetClientInsights`, (data) => {
            setInsightList(data);
            originalInsightList.current = data;
            setInsightListLoaded(true);
        });
    }

    const addClient = () => {
        get('ClientInsights', `AddClientInsight?client=${newClient}`, () => {
            getClientInsights();
            setNewClient('');
        })
    }

    const removeClient = (client: string) => {
        get('ClientInsights', `RemoveClientInsight?client=${client}`, () => {
            getClientInsights();
        })
    }

    const saveChanges = () => {
        post('ClientInsights', `UpdateClientInsight`, insightList, () => {
            getClientInsights();
        });
    }

    const handleChange = (client: string, event: React.ChangeEvent<HTMLInputElement>) => {
        setInsightList(prevList =>
            prevList.map(item =>
                item.client === client
                    ? { ...item, [event.target.name]: event.target.checked }
                    : item
            )
        );
    };

    return (
        (insightListLoaded && isAdmin) ?
            <Paper sx={styles.paper}>
                {unsavedChanges &&
                    <Alert variant="outlined" severity="warning">
                        There are unsaved changes. Please save to apply changes.
                    </Alert>
                }
                <Table sx={styles.table} stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Client</TableCell>
                            <TableCell>ResQ AI</TableCell>
                            <TableCell>Client Agent Insights</TableCell>
                            <TableCell>Client Operational Insights</TableCell>
                            <TableCell>Client Customer Insighs</TableCell>
                            <TableCell>Operations Agent Insights</TableCell>
                            <TableCell>Operations Operational Insights</TableCell>
                            <TableCell>Operations Customer Insights</TableCell>
                            <TableCell>Client Scorecard</TableCell>
                            <TableCell>Client Transcripts</TableCell>
                            <TableCell>Operations Scorecard</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {insightListLoaded && insightList.map(row => {
                            return (
                                <TableRow key={row.client}>
                                    <TableCell>{row.client}</TableCell>
                                    <TableCell><Switch name="resQAI" checked={row.resQAI} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="clientAgentInsights" checked={row.clientAgentInsights} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="clientOperationalInsights" checked={row.clientOperationalInsights} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="clientCustomerInsights" checked={row.clientCustomerInsights} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="operationsAgentInsights" checked={row.operationsAgentInsights} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="operationsOperationalInsights" checked={row.operationsOperationalInsights} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="operationsCustomerInsights" checked={row.operationsCustomerInsights} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="clientScorecard" checked={row.clientScorecard} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="clientTranscripts" checked={row.clientTranscripts} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell><Switch name="operationsScorecard" checked={row.operationsScorecard} onChange={(e) => handleChange(row.client, e)} /></TableCell>
                                    <TableCell>
                                        <IconButton color="error" onClick={() => removeClient(row.client)}><DeleteIcon /></IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                    <TableFooter>
                        <TableRow sx={{ position: 'sticky', bottom: 0, zIndex: 1, backgroundColor: '#fff', borderTop: '1px solid #e0e0e0' }}>
                            <TableCell>
                                <TextField label="Client" type="text" id="newClientName" key="newClientName" onChange={(e) => setNewClient(e.target.value)} value={newClient} />
                            </TableCell>
                            <TableCell>
                                <IconButton color="secondary" onClick={addClient}><AddIcon /></IconButton>
                            </TableCell>
                            {unsavedChanges &&
                                <>
                                    <TableCell>Save Changes</TableCell>
                                    <TableCell>
                                        <IconButton color="success" onClick={saveChanges}><SaveIcon /></IconButton>
                                    </TableCell>
                                </>
                            }
                        </TableRow>
                    </TableFooter>
                </Table>
            </Paper>
            :
            <React.Fragment />
    );
}
